import {
  ZadCustomer,
  ZadCustomerRefund,
  ZadCustomerRefundMethod,
  ZadCustomerRefundStatus,
  ZadCustomerRefundSummary,
  ZadCustomerRefundWritePayload,
  ZadCustomerReturn,
} from '../../../models/zad'
import {getZadCustomerDisplayName} from './customerHelpers'
import {
  getZadCustomerReturnCustomerId,
  isWalkInZadCustomerReturn,
} from './customerReturnHelpers'
import {ZadApiError, resolveZadUserMessage} from './errors'
import {parseZadPriceInput, roundZadMoney3} from './productHelpers'

export const ZAD_CUSTOMER_REFUND_STATUS_I18N: Record<ZadCustomerRefundStatus, string> = {
  ACTIVE: 'ZAD.CUSTOMER_REFUNDS.STATUS_ACTIVE',
  CANCELLED: 'ZAD.CUSTOMER_REFUNDS.STATUS_CANCELLED',
}

export const ZAD_CUSTOMER_REFUND_METHOD_I18N: Record<ZadCustomerRefundMethod, string> = {
  CASH: 'ZAD.CUSTOMER_REFUNDS.METHOD_CASH',
  BANK_TRANSFER: 'ZAD.CUSTOMER_REFUNDS.METHOD_BANK',
  CHEQUE: 'ZAD.CUSTOMER_REFUNDS.METHOD_CHEQUE',
  CARD: 'ZAD.CUSTOMER_REFUNDS.METHOD_CARD',
  OTHER: 'ZAD.CUSTOMER_REFUNDS.METHOD_OTHER',
}

export const ZAD_CUSTOMER_REFUND_METHODS: ZadCustomerRefundMethod[] = [
  'CASH',
  'BANK_TRANSFER',
  'CHEQUE',
  'CARD',
  'OTHER',
]

export type ZadCustomerRefundEditorValues = {
  customer: string
  refundDate: string
  amount: string
  refundMethod: ZadCustomerRefundMethod
  reference: string
  reason: string
  notes: string
  sourceReturn: string
}

export function todayZadRefundIsoDate(): string {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
}

export function emptyZadCustomerRefundEditorValues(): ZadCustomerRefundEditorValues {
  return {
    customer: '',
    refundDate: todayZadRefundIsoDate(),
    amount: '',
    refundMethod: 'CASH',
    reference: '',
    reason: '',
    notes: '',
    sourceReturn: '',
  }
}

export function canCancelZadCustomerRefund(status?: ZadCustomerRefundStatus | string): boolean {
  return status === 'ACTIVE'
}

export function parseZadCustomerRefundAmount(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false; errorId: string} {
  const parsed = parseZadPriceInput(value, {required: true})
  if (!parsed.ok) return {ok: false, errorId: 'ZAD.CUSTOMER_REFUNDS.AMOUNT_REQUIRED'}
  if (parsed.value <= 0) return {ok: false, errorId: 'ZAD.CUSTOMER_REFUNDS.AMOUNT_INVALID'}
  return {ok: true, value: parsed.value}
}

export function refundableCreditFromSummary(
  summary?: Pick<ZadCustomerRefundSummary, 'refundableCredit'> | null
): number {
  if (!summary || summary.refundableCredit == null) return 0
  return roundZadMoney3(summary.refundableCredit)
}

export function refundCapFromReturnAndSummary(
  summaryCredit: number,
  remainingRefundableAmount?: number | null
): number {
  if (remainingRefundableAmount == null) return roundZadMoney3(summaryCredit)
  return roundZadMoney3(Math.min(summaryCredit, Number(remainingRefundableAmount) || 0))
}

export function validateZadRefundAmountAgainstCredit(
  amountValue: string | number,
  refundableCredit: number
): string | null {
  const amount = parseZadCustomerRefundAmount(amountValue)
  if (!amount.ok) return amount.errorId
  const credit = roundZadMoney3(refundableCredit)
  if (credit <= 0) return 'ZAD.CUSTOMER_REFUNDS.NO_CREDIT'
  if (amount.value > credit) return 'ZAD.CUSTOMER_REFUNDS.AMOUNT_EXCEEDS'
  return null
}

export function validateZadCustomerRefundForm(
  values: ZadCustomerRefundEditorValues,
  refundableCredit: number
): string | null {
  if (!String(values.customer || '').trim()) return 'ZAD.CUSTOMER_REFUNDS.CUSTOMER_REQUIRED'
  if (!String(values.refundDate || '').trim()) return 'ZAD.CUSTOMER_REFUNDS.DATE_REQUIRED'
  if (!values.refundMethod) return 'ZAD.CUSTOMER_REFUNDS.METHOD_REQUIRED'
  return validateZadRefundAmountAgainstCredit(values.amount, refundableCredit)
}

export function sanitizeZadCustomerRefundWritePayload(
  payload: ZadCustomerRefundWritePayload
): ZadCustomerRefundWritePayload {
  const customer = String(payload.customer || '').trim()
  const body: ZadCustomerRefundWritePayload = {
    refundDate: String(payload.refundDate || '').trim(),
    amount: Number(payload.amount) || 0,
    refundMethod: payload.refundMethod,
    reference: payload.reference != null ? String(payload.reference) : '',
    reason: String(payload.reason || '').trim(),
    notes: String(payload.notes || '').trim(),
  }
  if (customer) body.customer = customer
  else body.customer = ''
  const sourceReturn = String(payload.sourceReturn || '').trim()
  if (sourceReturn) body.sourceReturn = sourceReturn
  const sourceSale = String(payload.sourceSale || '').trim()
  if (sourceSale) body.sourceSale = sourceSale
  return body
}

export function toZadCustomerRefundPayload(
  values: ZadCustomerRefundEditorValues
): ZadCustomerRefundWritePayload {
  const amount = parseZadCustomerRefundAmount(values.amount)
  return sanitizeZadCustomerRefundWritePayload({
    customer: values.customer,
    refundDate: values.refundDate,
    amount: amount.ok ? amount.value : 0,
    refundMethod: values.refundMethod,
    reference: values.reference,
    reason: values.reason,
    notes: values.notes,
    sourceReturn: values.sourceReturn,
  })
}

export function formatZadCustomerRefundCustomer(
  customer: ZadCustomerRefund['customer'] | ZadCustomer | string | null | undefined,
  snapshot?: ZadCustomerRefund['customerSnapshot']
): string {
  if (snapshot?.displayName) return snapshot.displayName
  if (!customer) return snapshot?.customerNumber || ''
  if (typeof customer === 'string') return customer
  return getZadCustomerDisplayName(customer)
}

export function getZadCustomerRefundCustomerId(
  customer: ZadCustomerRefund['customer'] | string | null | undefined
): string {
  if (!customer) return ''
  if (typeof customer === 'string') return customer
  return customer._id || ''
}

export function getZadCustomerRefundSourceReturnId(
  sourceReturn: ZadCustomerRefund['sourceReturn']
): string {
  if (!sourceReturn) return ''
  if (typeof sourceReturn === 'string') return sourceReturn
  return sourceReturn._id || ''
}

export function getZadCustomerRefundSourceReturnNumber(refund?: ZadCustomerRefund | null): string {
  if (!refund) return ''
  if (refund.sourceReturnNumber) return refund.sourceReturnNumber
  if (refund.sourceReturn && typeof refund.sourceReturn !== 'string') {
    return refund.sourceReturn.returnNumber || ''
  }
  return ''
}

export function isSelectableZadRefundSourceReturn(
  ret: Pick<ZadCustomerReturn, 'status' | 'customer'> | null | undefined,
  customerId: string
): boolean {
  if (!ret || !customerId) return false
  if (ret.status !== 'VALIDATED') return false
  if (isWalkInZadCustomerReturn(ret)) return false
  return getZadCustomerReturnCustomerId(ret) === customerId
}

export function canOfferZadCustomerRefundFromReturn(
  ret?: Pick<ZadCustomerReturn, 'status' | 'customer' | 'remainingRefundableAmount'> | null
): boolean {
  if (!ret) return false
  if (ret.status !== 'VALIDATED') return false
  if (ret.remainingRefundableAmount != null) return Number(ret.remainingRefundableAmount) > 0
  if (isWalkInZadCustomerReturn(ret)) return false
  if (!getZadCustomerReturnCustomerId(ret)) return false
  return true
}

export function canOfferZadCustomerRefundAction(refundableCredit?: number | null): boolean {
  return roundZadMoney3(refundableCredit || 0) > 0
}

export function buildZadCustomerRefundNewPath(customerId?: string, sourceReturnId?: string): string {
  const params = new URLSearchParams()
  if (customerId) params.set('customer', customerId)
  if (sourceReturnId) params.set('sourceReturn', sourceReturnId)
  const query = params.toString()
  return query ? `/zad/customer-refunds/new?${query}` : '/zad/customer-refunds/new'
}

export function formatZadRefundExceededDetails(details?: {
  requested?: number
  refundableCredit?: number
}): {requested: number; refundableCredit: number} {
  return {
    requested: Number(details?.requested) || 0,
    refundableCredit: Number(details?.refundableCredit) || 0,
  }
}

export function resolveZadCustomerRefundMessage(
  err: ZadApiError
): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    CUSTOMER_NOT_FOUND: 'ZAD.CUSTOMERS.NOT_FOUND',
    CUSTOMER_INACTIVE: 'ZAD.SALES.CUSTOMER_INACTIVE',
    NO_CUSTOMER_CREDIT: 'ZAD.CUSTOMER_REFUNDS.NO_CREDIT',
    REFUND_EXCEEDS_CREDIT: 'ZAD.CUSTOMER_REFUNDS.AMOUNT_EXCEEDS',
    REFUND_NOT_FOUND: 'ZAD.CUSTOMER_REFUNDS.NOT_FOUND',
    REFUND_ALREADY_CANCELLED: 'ZAD.CUSTOMER_REFUNDS.ALREADY_CANCELLED',
    RETURN_NOT_FOUND: 'ZAD.CUSTOMER_REFUNDS.RETURN_NOT_FOUND',
    RETURN_NOT_VALIDATED: 'ZAD.CUSTOMER_REFUNDS.RETURN_NOT_VALIDATED',
    RETURN_HAS_NO_CUSTOMER: 'ZAD.CUSTOMER_REFUNDS.RETURN_HAS_NO_CUSTOMER',
    RETURN_CUSTOMER_MISMATCH: 'ZAD.CUSTOMER_REFUNDS.RETURN_CUSTOMER_MISMATCH',
    INVALID_REFUND_AMOUNT: 'ZAD.CUSTOMER_REFUNDS.AMOUNT_INVALID',
    VALIDATION_ERROR: 'ZAD.CUSTOMER_REFUNDS.VALIDATION_ERROR',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}
